// Levels of Ocean disco. The id is fixed (progress is saved under it) – never change it once the level is published.
import { defineLevels } from '../../core/registry';
import type { LevelData, StaticData } from '../../core/types';

const P = 16;
const stone = (x: number, y: number, w: number, h?: number, a?: number): StaticData => ({ x, y, w, h: h || 40, a: a || 0, skin: 'ocean_stone' });
const coral = (x: number, y: number, w: number, h?: number, a?: number): StaticData => ({ x, y, w, h: h || 22, a: a || 0, skin: 'ocean_coral' });
const sand = (x: number, y: number, w: number, h?: number): StaticData => ({ x, y, w, h: h || 60, skin: 'ocean_sand' });
const L: LevelData[] = [];

// 1 – Sink – under water the pearl sinks slowly – a kelp branch leads it home into the shell (new: water)
L.push({
  id: 'sink', par: 1, tools: { plank: 2 }, learn: ['gravity', 'ramp'], intro: 'ocean_water', time: 30,
  statics: [coral(200, 260, 300, 22, 18), sand(600, 780, 1200, 40)],
  balls: [{ x: 80, y: 196, r: P }],
  goal: { type: 'bowl', x: 580, y: 760, w: 200 },
  objs: [{ t: 'duck', x: 960, y: 500 }],
  solution: [{ t: 'plank', x: 440, y: 520, a: 30 }],
});

// 2 – Bubbles – the bubble machine blows the pearl up to the treasure chest (new: bubble machine)
L.push({
  id: 'bubbles', par: 1, tools: { ocean_bubbles: 1 }, learn: ['materials', 'energy'], intro: 'ocean_bubbles', time: 30,
  statics: [coral(200, 260, 300, 22, 18), sand(600, 780, 1200, 40)],
  balls: [{ x: 80, y: 196, r: P }],
  goal: { type: 'bell', x: 390, y: 220 },
  objs: [{ t: 'duck', x: 960, y: 500 }],
  solution: [{ t: 'ocean_bubbles', x: 380, y: 700, a: 0 }],
});

// 3 – The current – the conch blows a current that pushes the pearl over the shell (new: conch)
L.push({
  id: 'current', par: 1, tools: { ocean_conch: 1 }, learn: ['direction', 'energy'], intro: 'ocean_conch', time: 30,
  statics: [coral(200, 260, 300, 22, 18), sand(600, 780, 1200, 40)],
  balls: [{ x: 80, y: 196, r: P }],
  goal: { type: 'bowl', x: 660, y: 760, w: 200 },
  objs: [{ t: 'duck', x: 700, y: 300 }],
  solution: [{ t: 'ocean_conch', x: 300, y: 480, a: 0 }],
});

// 4 – Bubble balloon – the balloon carries the pearl up, the current takes it over the shell and the urchin pops it (new: bubble balloon, current, urchin)
L.push({
  id: 'bubble-balloon', par: 1, tools: { ocean_balloon: 1 }, learn: ['materials', 'gravity'], intro: 'ocean_balloon', time: 34,
  statics: [coral(200, 600, 300, 22, 12), sand(600, 780, 1200, 40), stone(820, 620, 300, 60)],
  balls: [{ x: 80, y: 566, r: P }],
  goal: { type: 'bowl', x: 820, y: 590, w: 200 },
  objs: [{ t: 'ocean_current', x: 280, y: 260, a: 0, len: 640, wid: 140, v: 3.5 }, { t: 'ocean_urchin', x: 610, y: 150, r: 24 }, { t: 'duck', x: 1000, y: 400 }],
  solution: [{ t: 'ocean_balloon', x: 420, y: 700 }],
  fails: [[{ t: 'ocean_balloon', x: 200, y: 400 }]],
});

// 5 – The float – the anchor sinks onto the button, the float fills with air and lifts the pearl up to the chest (new: submarine float)
L.push({
  id: 'float', par: 1, tools: { stone: 1, plank: 1 }, learn: ['materials', 'chain_reaction'], intro: 'ocean_float', time: 34,
  statics: [sand(600, 780, 1200, 40), stone(260, 420, 220, 30)],
  balls: [{ x: 700, y: 712, r: P }],
  goal: { type: 'bell', x: 700, y: 260 },
  objs: [{ t: 'ocean_float', x: 700, y: 745, w: 160, id: 'air', minY: 330 }, { t: 'button', x: 260, y: 395, act: { fire: 'air' } }, { t: 'duck', x: 980, y: 400 }],
  solution: [{ t: 'stone', x: 260, y: 220 }],
  fails: [[{ t: 'stone', x: 480, y: 220 }], [{ t: 'plank', x: 260, y: 300, a: 15 }]],
});

// 6 – Against the current – the current pushes the pearl away – the conch blows it back the other way
L.push({
  id: 'countercurrent', par: 1, tools: { ocean_conch: 1, ocean_bubbles: 1 }, learn: ['direction', 'energy'], time: 34,
  statics: [coral(1000, 260, 300, 22, -18), sand(600, 780, 1200, 40)],
  balls: [{ x: 1120, y: 196, r: P }],
  goal: { type: 'bowl', x: 620, y: 760, w: 200 },
  objs: [{ t: 'ocean_current', x: 760, y: 560, a: 0, len: 440, wid: 120, v: 2.5 }, { t: 'duck', x: 200, y: 300 }],
  solution: [{ t: 'ocean_conch', x: 1000, y: 420, a: 180 }],
  fails: [[{ t: 'ocean_conch', x: 1000, y: 420, a: 0 }], [{ t: 'ocean_bubbles', x: 830, y: 745, a: 0 }]],
});

// 7 – The jellyfish – the pearl hops on the jellyfish over the rock – the water slows the hop (new: jellyfish)
L.push({
  id: 'jellyfish', par: 1, tools: { ocean_jellyfish: 1, plank: 1 }, learn: ['spring', 'gravity'], intro: 'ocean_jellyfish', time: 34,
  statics: [coral(200, 260, 300, 22, 18), sand(600, 780, 1200, 40), stone(500, 640, 40, 240)],
  balls: [{ x: 80, y: 196, r: P }],
  goal: { type: 'bowl', x: 660, y: 760, w: 200 },
  objs: [{ t: 'duck', x: 500, y: 380 }],
  solution: [{ t: 'ocean_jellyfish', x: 360, y: 560, a: 30 }],
  fails: [[{ t: 'plank', x: 360, y: 560, a: 15 }], [{ t: 'ocean_jellyfish', x: 360, y: 560, a: -30 }]],
});

// 8 – The bubble pump – the kelp branch leads the pearl onto the button that starts the bubble pump under it (new: bubble pump)
L.push({
  id: 'bubble-pump', par: 1, tools: { plank: 1, ocean_conch: 1 }, learn: ['chain_reaction', 'energy'], time: 34,
  statics: [coral(200, 260, 300, 22, 18), sand(600, 780, 1200, 40)],
  balls: [{ x: 80, y: 196, r: P }],
  goal: { type: 'bell', x: 700, y: 360 },
  objs: [{ t: 'button', x: 540, y: 745, act: { fire: 'pumpe' } }, { t: 'ocean_pump', x: 540, y: 776, a: 20, id: 'pumpe', len: 450 }, { t: 'duck', x: 300, y: 600 }],
  solution: [{ t: 'plank', x: 440, y: 560, a: 30 }],
  fails: [[{ t: 'plank', x: 440, y: 560, a: -30 }], [{ t: 'ocean_conch', x: 300, y: 480, a: 180 }]],
});

// 9 – Empty the float – the anchor presses the button, the air is let out and the float sinks with the pearl down to the chest
L.push({
  id: 'empty-the-float', par: 1, tools: { stone: 1, ocean_balloon: 1 }, learn: ['materials', 'chain_reaction'], time: 34,
  statics: [sand(600, 780, 1200, 40), stone(1000, 440, 220, 30)],
  balls: [{ x: 640, y: 222, r: P }],
  goal: { type: 'bell', x: 640, y: 600 },
  objs: [{ t: 'ocean_float', x: 640, y: 255, w: 160, air: 1, id: 'air', minY: 255, maxY: 720 }, { t: 'button', x: 1000, y: 415, act: { fire: 'air' } }, { t: 'duck', x: 300, y: 500 }],
  solution: [{ t: 'stone', x: 1000, y: 250 }],
  fails: [[{ t: 'stone', x: 820, y: 250 }], [{ t: 'ocean_balloon', x: 1000, y: 330 }]],
});

// 10 – Bubble and current – the bubbles lift the pearl into the current, which carries it over the shell
L.push({
  id: 'bubble-and-current', par: 1, tools: { ocean_bubbles: 1, ocean_conch: 1 }, learn: ['energy', 'direction'], time: 36,
  statics: [coral(200, 260, 300, 22, 18), sand(600, 780, 1200, 40), stone(780, 580, 300, 30)],
  balls: [{ x: 80, y: 196, r: P }],
  goal: { type: 'bowl', x: 780, y: 565, w: 220 },
  objs: [{ t: 'ocean_current', x: 300, y: 300, a: 0, len: 480, wid: 200, v: 3 }, { t: 'duck', x: 600, y: 650 }],
  solution: [{ t: 'ocean_bubbles', x: 380, y: 745, a: 0 }],
  fails: [[{ t: 'ocean_conch', x: 300, y: 600, a: 0 }], [{ t: 'ocean_bubbles', x: 700, y: 745, a: 0 }]],
});

// 11 – Steer the balloon – the conch's current steers the bubble balloon to the urchin above the shell
L.push({
  id: 'steer-the-balloon', par: 2, tools: { ocean_balloon: 1, ocean_conch: 1, ocean_bubbles: 1 }, learn: ['direction', 'materials'], time: 36,
  statics: [coral(200, 600, 300, 22, 12), sand(600, 780, 1200, 40), stone(720, 620, 300, 60)],
  balls: [{ x: 80, y: 566, r: P }],
  goal: { type: 'bowl', x: 720, y: 590, w: 200 },
  objs: [{ t: 'ocean_urchin', x: 610, y: 140, r: 24 }, { t: 'duck', x: 1000, y: 400 }],
  solution: [{ t: 'ocean_balloon', x: 420, y: 700 }, { t: 'ocean_conch', x: 330, y: 250, a: 0 }],
  fails: [[{ t: 'ocean_balloon', x: 420, y: 700 }], [{ t: 'ocean_balloon', x: 420, y: 700 }, { t: 'ocean_conch', x: 330, y: 250, a: 180 }]],
});

// 12 – Hop and bubbles – the jellyfish sends the pearl over the rock, the bubbles lift it up to the chest
L.push({
  id: 'hop-and-bubbles', par: 2, tools: { ocean_jellyfish: 1, ocean_bubbles: 1, plank: 1 }, learn: ['spring', 'energy'], time: 36,
  statics: [coral(200, 260, 300, 22, 18), sand(600, 780, 1200, 40), stone(500, 640, 40, 240)],
  balls: [{ x: 80, y: 196, r: P }],
  goal: { type: 'bell', x: 596, y: 290 },
  objs: [{ t: 'duck', x: 900, y: 500 }],
  solution: [{ t: 'ocean_jellyfish', x: 360, y: 560, a: 30 }, { t: 'ocean_bubbles', x: 600, y: 740, a: 0 }],
  fails: [[{ t: 'ocean_jellyfish', x: 360, y: 560, a: 30 }], [{ t: 'ocean_bubbles', x: 380, y: 745, a: 0 }]],
});

// 13 – Float and current – the float lifts the pearl into the current, which pushes it down into the shell
L.push({
  id: 'float-and-current', par: 2, tools: { stone: 1, ocean_conch: 1, ocean_bubbles: 1 }, learn: ['materials', 'direction', 'chain_reaction'], time: 40,
  statics: [sand(600, 780, 1200, 40), stone(220, 420, 200, 30), stone(870, 480, 240, 30)],
  balls: [{ x: 600, y: 712, r: P }],
  goal: { type: 'bowl', x: 860, y: 465, w: 200 },
  objs: [{ t: 'ocean_float', x: 600, y: 745, w: 160, id: 'air', minY: 300 }, { t: 'button', x: 220, y: 395, act: { fire: 'air' } }, { t: 'duck', x: 300, y: 650 }],
  solution: [{ t: 'stone', x: 220, y: 220 }, { t: 'ocean_conch', x: 470, y: 250, a: 0 }],
  fails: [[{ t: 'stone', x: 220, y: 220 }], [{ t: 'ocean_conch', x: 470, y: 250, a: 0 }], [{ t: 'stone', x: 220, y: 220 }, { t: 'ocean_conch', x: 470, y: 250, a: 180 }]],
});

// 14 – The balloon journey – combine: the kelp branch sends the pearl into the bubble balloon, the conch steers it to the urchin above the shell
L.push({
  id: 'balloon-journey', par: 3, tools: { plank: 1, ocean_balloon: 1, ocean_conch: 1, ocean_jellyfish: 1 }, learn: ['ramp', 'materials', 'direction'], time: 40,
  statics: [coral(200, 260, 300, 22, 18), sand(600, 780, 1200, 40), stone(860, 620, 300, 60)],
  balls: [{ x: 80, y: 196, r: P }],
  goal: { type: 'bowl', x: 860, y: 590, w: 200 },
  objs: [{ t: 'ocean_urchin', x: 760, y: 140, r: 24 }, { t: 'duck', x: 560, y: 560 }],
  solution: [{ t: 'plank', x: 440, y: 560, a: 30 }, { t: 'ocean_balloon', x: 560, y: 690 }, { t: 'ocean_conch', x: 500, y: 250, a: 0 }],
  fails: [[{ t: 'ocean_balloon', x: 560, y: 690 }, { t: 'ocean_conch', x: 500, y: 250, a: 0 }], [{ t: 'plank', x: 440, y: 560, a: 30 }, { t: 'ocean_balloon', x: 560, y: 690 }]],
});

// 15 – Master – master: the anchor fills the float with air, the conch pushes the pearl into the bubbles, and the bubbles lift it up to the chest
L.push({
  id: 'master', par: 3, tools: { stone: 1, ocean_conch: 1, ocean_bubbles: 1, ocean_balloon: 1, ocean_jellyfish: 1 }, learn: ['materials', 'direction', 'energy', 'chain_reaction'], time: 44,
  statics: [sand(600, 780, 1200, 40), stone(160, 420, 200, 30), stone(860, 420, 200, 30)],
  balls: [{ x: 420, y: 712, r: P }],
  goal: { type: 'bell', x: 640, y: 290 },
  objs: [{ t: 'ocean_float', x: 420, y: 745, w: 160, id: 'air', minY: 330 }, { t: 'button', x: 160, y: 395, act: { fire: 'air' } }, { t: 'ocean_urchin', x: 860, y: 380, r: 22 }, { t: 'duck', x: 1000, y: 200 }],
  solution: [{ t: 'stone', x: 160, y: 220 }, { t: 'ocean_conch', x: 280, y: 300, a: 0 }, { t: 'ocean_bubbles', x: 630, y: 745, a: 0 }],
  fails: [[{ t: 'stone', x: 160, y: 220 }, { t: 'ocean_conch', x: 280, y: 300, a: 0 }], [{ t: 'stone', x: 160, y: 220 }, { t: 'ocean_bubbles', x: 630, y: 745, a: 0 }], [{ t: 'ocean_conch', x: 280, y: 300, a: 0 }, { t: 'ocean_bubbles', x: 630, y: 745, a: 0 }]],
});

export const levels = defineLevels(L);
